/* eslint-disable import/no-cycle */
import React, { memo, useEffect } from "react";

import { useFormContext } from "react-hook-form";

import { Box, Preloader } from "@origin-digital/ods-core";
import { task } from "@origin-digital/reporting-client";

import {
  RouterActions,
  StyledHeading,
  StyledQuestion,
  StyledText,
} from "@/components";
import { ANALYTICS_FORM_NAME, ANALYTICS_TASK_TYPE } from "@/constants";
import { SolarOutlookWrapper, useSolarOutlook } from "@/features/SolarOutlook";
import { useFieldValidation } from "@/hooks";

import { recommendation } from "./content.json";

export const Recommendation = memo(
  ({
    backRoute,
    nextRoute,
    hardStopRoute,
  }: {
    backRoute: () => Promise<string>;
    nextRoute: () => Promise<string>;
    hardStopRoute: () => Promise<string>;
  }) => {
    const { loading, solarOutlook, medianSizeIndex } = useSolarOutlook({
      hardStopRoute,
    });
    const { getValues } = useFormContext();

    const { isValid } = useFieldValidation({ fields: ["selectedSystemSize"] });

    useEffect(() => {
      const medianSystemData = solarOutlook.systemData?.at(medianSizeIndex);
      if (medianSystemData && !loading) {
        const billFrequency: string | undefined =
          medianSystemData?.billSavingPeriod;
        const recommendSystemSize: number | undefined = medianSystemData?.size;
        const averageBillReduction: number | undefined =
          medianSystemData?.billSavingPercent;
        const savingsEstimate: number | undefined =
          medianSystemData?.totalBillAfterSavingForBillFrequency;
        const systemType = getValues("systemType");

        task.base(ANALYTICS_TASK_TYPE, {
          action: "update",
          formName: ANALYTICS_FORM_NAME,
          currentAction: {
            billFrequency,
            recommendSystemSize,
            averageBillReduction,
            savingsEstimate,
            systemType,
          },
        });
      }
    }, [loading, medianSizeIndex, solarOutlook.systemData]);

    return (
      <StyledQuestion>
        <div>
          <StyledHeading data-id={recommendation.id} tone="neutralDark">
            {recommendation.heading}
          </StyledHeading>
          {loading ? (
            <Box
              data-id="preloader-wrapper"
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{ height: "50vh" }}
            >
              <Preloader />
            </Box>
          ) : (
            <>
              <StyledText tone="neutralDark">
                {recommendation.description}
              </StyledText>
              <br />
              <SolarOutlookWrapper
                medianSizeIndex={medianSizeIndex}
                solarOutlook={solarOutlook}
              />
              <RouterActions
                onBackRoute={backRoute}
                onNextRoute={nextRoute}
                onNext={isValid}
              />
            </>
          )}
        </div>
      </StyledQuestion>
    );
  }
);
